import { useCallback, useEffect, type PointerEvent as ReactPointerEvent } from 'react'
import { AudioLines, Boxes, Cctv, ClipboardCopy, Gauge, MonitorPlay, PlugZap, RefreshCw, Settings2, TriangleAlert } from 'lucide-react'
import { usePermissions, useToast } from '../../admin/primitives.js'
import {
  allerAncre, composerDeclencheurs, copierTexte, entreesLien, estPageCourante, useMenuContextuel,
  type ActionMenu, type DeclencheursComposes, type GroupeMenu,
} from '../../shell/menu-contextuel.js'
import { useGroupeFavori } from '../../shell/menu-favori.js'
import { lireSupervision } from '../api.js'
import { estSchema, type SchemaReglages } from '../reglages/modele.js'
import { nomSalle } from './commun.js'
import { actualisationEnCours, actualiser, type CibleActualisation } from './actualiser.js'
import { cibleValide } from './actualiser-modele.js'
import { cheminSalle } from './fiches/modele.js'
import {
  ANCRE_CAMERAS, ANCRES_SALLE, CHEMIN_AUDIOVISUEL, CHEMIN_MODE_SOIREE, CHEMIN_REGLAGES, CHEMIN_VIDEO,
  droitDestination, ecransReglagesSalle, estCodeSalle,
} from './menu-salle-modele.js'

/**
 * MENU CLIC DROIT D'UNE SALLE (lot 2, 24/10/2026) — et des tuiles de domaine
 * du hub. NAVIGATION, aucune fenêtre ; pour seuls gestes l'épingle des
 * favoris, celle de l'étoile (useGroupeFavori, lot 6), et « Actualiser la
 * salle » (25/10/2026), le bouton de la fiche salle (actualiser.tsx).
 *
 * Chaque entrée reprend un lien qui existe déjà, avec le droit de la page
 * qu'il ouvre :
 *
 *   · Ouvrir la salle, nouvel onglet      → /infrastructure/salles/:code (sonde.read)
 *   · Actualiser la salle                 → POST zones/:code/refresh, sans notification
 *                                           (sonde.read)
 *   · Incidents, appareils, alimentation,
 *     score                               → sections de la fiche salle (sonde.read)
 *   · Son et lumière                      → /technique/audiovisuel (sonde.read)
 *   · Vidéosurveillance                   → toutes les caméras, par salle (sonde.read)
 *   · Mode soirée                         → /infrastructure/live (sonde.read)
 *   · Régler la supervision               → Paramètres › Supervision, ou l'écran d'un
 *                                           groupe à la portée de la salle (lien de la
 *                                           fiche, sonde.settings.manage)
 *   · Ajouter aux favoris                 → la fiche salle, sous le nom ROOM 1… ;
 *                                           une tuile du hub, sous son titre
 *   · Copier le nom (ROOM 1…) et le lien
 *
 * Pas de note sur la salle, pas de maintenance de salle, pas de « portes de
 * la salle » : aucune page ne propose ces gestes aujourd'hui (à créer d'abord).
 */

export interface SalleMenu { code: string; label?: string | null }
/** Une tuile de domaine du hub : sa page, son titre. */
export interface DestinationMenu { href: string; titre: string }

/* ─── schéma des réglages : une lecture partagée, pour qui peut régler ─── */

let schema: SchemaReglages | null = null
let schemaLuA = 0
let lectureSchema: Promise<void> | null = null

/** Relu au plus toutes les 5 min ; un échec laisse l'entrée simple vers Paramètres › Supervision. */
function relireSchema(): void {
  if (lectureSchema || Date.now() - schemaLuA < 300_000) return
  lectureSchema = lireSupervision('/v/settings/schema', estSchema).then((l) => {
    // Un échec se retente une minute plus tard, pas à chaque montage.
    if (l.etat === 'ok') { schema = l.data; schemaLuA = Date.now() } else schemaLuA = Date.now() - 240_000
    lectureSchema = null
  })
}

/**
 * Plein écran (Mode soirée) : le menu et ses toasts sont montés dans `body`,
 * hors de l'élément plein écran — ils seraient invisibles. Le navigateur
 * garde alors la main, comme sans menu.
 */
function pleinEcranAilleurs(): boolean {
  if (typeof document === 'undefined') return false
  const f = document.fullscreenElement
  return !!f && f !== document.documentElement && f !== document.body
}

/**
 * Un lien de salle DANS un élément qui porte déjà un menu (la ligne « famille
 * · type · salle » du hero de la fiche appareil). Au doigt, chaque menu arme
 * son minuteur d'appui long ; celui du parent, armé en dernier, l'emportait :
 * l'appui long sur la salle ouvrait le menu de l'appareil. L'appui ne remonte
 * donc pas au parent. La souris et le clavier s'arrêtent déjà au menu le plus
 * proche.
 */
const garderAppui = (e: ReactPointerEvent<HTMLElement>) => { if (e.pointerType === 'touch') e.stopPropagation() }

/* ─── le hook ─── */

/**
 * Les déclencheurs du menu d'une salle, ou d'une tuile de domaine :
 *
 *     const menuSalle = useMenuSalle()
 *     <Link href=… {...menuSalle.salle({ code: z.code, label: z.label })}>
 *     <Link href={d.href} {...menuSalle.destination({ href: d.href, titre: d.titre })}>
 */
export function useMenuSalle() {
  const menu = useMenuContextuel()
  const toast = useToast()
  const { permissions } = usePermissions()
  // Droits pas encore lus : tout est faux, le navigateur garde son menu.
  const peutLire = permissions.includes('sonde.read')
  const peutReglages = permissions.includes('sonde.settings.manage')
  const favori = useGroupeFavori()
  useEffect(() => { if (peutReglages) relireSchema() }, [peutReglages])

  const actionsSalle = useCallback((z: SalleMenu): GroupeMenu[] => {
    const code = z.code.toUpperCase()
    if (!peutLire || !estCodeSalle(code) || pleinEcranAilleurs()) return []
    const nom = nomSalle(code, z.label)
    const fiche = cheminSalle(code)
    const lien = entreesLien(fiche, toast, { ouvrir: 'Ouvrir la salle' })
    const section = (id: string, libelle: string, icone: ActionMenu['icone'], ancre: string): ActionMenu =>
      ({ id, libelle, icone, agir: () => allerAncre(fiche, ancre) })

    const cible: CibleActualisation = { kind: 'zone', code, nom }
    const groupes: GroupeMenu[] = [
      { actions: lien.filter((a) => a.id !== 'c-lien') },
      ...(cibleValide(cible) ? [{ actions: [{
        id: 'actualiser', libelle: 'Actualiser la salle', icone: <RefreshCw size={15} />, agir: () => { void actualiser(cible) },
        ...(actualisationEnCours(cible) ? { indisponible: 'Actualisation en cours' } : {}),
      }] }] : []),
      { titre: 'La salle', actions: [
        section('incidents', 'Voir les incidents de la salle', <TriangleAlert size={15} />, ANCRES_SALLE.incidents),
        section('appareils', 'Voir les appareils de la salle', <Boxes size={15} />, ANCRES_SALLE.appareils),
        section('alimentation', 'Voir l’alimentation', <PlugZap size={15} />, ANCRES_SALLE.alimentation),
        section('score', 'Voir le score de la salle', <Gauge size={15} />, ANCRES_SALLE.score),
      ] },
    ]

    // Pas d'entrée vers la page déjà affichée : le clic ne mènerait nulle part.
    const ailleurs: ActionMenu[] = []
    if (!estPageCourante(CHEMIN_AUDIOVISUEL)) ailleurs.push({ id: 'av', libelle: 'Son et lumière', icone: <AudioLines size={15} />, href: CHEMIN_AUDIOVISUEL })
    ailleurs.push({ id: 'video', libelle: 'Vidéosurveillance — toutes les caméras, par salle', icone: <Cctv size={15} />, agir: () => allerAncre(CHEMIN_VIDEO, ANCRE_CAMERAS) })
    if (!estPageCourante(CHEMIN_MODE_SOIREE)) ailleurs.push({ id: 'live', libelle: 'Mode soirée', icone: <MonitorPlay size={15} />, href: CHEMIN_MODE_SOIREE })
    groupes.push({ actions: ailleurs })

    if (peutReglages) {
      const ecrans = ecransReglagesSalle(schema, code)
      groupes.push({ actions: [ecrans.length > 0
        ? { id: 'regler', libelle: `Régler la supervision de ${nom}`, icone: <Settings2 size={15} />, sousMenu: [
          ...ecrans.map((e) => ({ id: `regler-${e.code}`, libelle: e.libelle, href: e.href })),
          { id: 'regler-tout', libelle: 'Tous les réglages de la supervision', href: CHEMIN_REGLAGES },
        ] }
        : { id: 'regler', libelle: 'Régler la supervision', icone: <Settings2 size={15} />, href: CHEMIN_REGLAGES }] })
    }

    groupes.push(favori(fiche, nom))
    groupes.push({ titre: 'Copier', actions: [
      { id: 'c-nom', libelle: 'Copier le nom', icone: <ClipboardCopy size={15} />, agir: () => copierTexte(nom, 'nom', toast) },
      ...lien.filter((a) => a.id === 'c-lien'),
    ] })
    return groupes
  }, [peutLire, peutReglages, toast, favori])

  const actionsDestination = useCallback((d: DestinationMenu): GroupeMenu[] => {
    if (!permissions.includes(droitDestination(d.href)) || pleinEcranAilleurs()) return []
    const lien = entreesLien(d.href, toast)
    return [
      { actions: lien.filter((a) => a.id !== 'c-lien') },
      favori(d.href, d.titre),
      { titre: 'Copier', actions: lien.filter((a) => a.id === 'c-lien') },
    ]
  }, [permissions, toast, favori])

  return {
    /** `imbrique` : le lien vit dans un élément qui porte déjà un menu. */
    salle: (z: SalleMenu, opts: { imbrique?: boolean } = {}): DeclencheursComposes => {
      const d = menu(() => actionsSalle(z), nomSalle(z.code.toUpperCase(), z.label))
      return opts.imbrique && 'data-menu-contextuel' in d ? composerDeclencheurs(d, { onPointerDown: garderAppui }) : d
    },
    destination: (d: DestinationMenu): DeclencheursComposes => menu(() => actionsDestination(d), d.titre),
  }
}
